import { FC, useMemo, useState } from "react";
import { HTUpgradeDataCard } from "react-migration/components/HTUpgradeDataCard";
import { UpgradeEventLocation } from "react-migration/lib/util/logEventV2";
import { ZooplaListingsDTO } from "react-migration/domains/comparables/typings/Zoopla";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { PropertyImageCarousel } from "react-migration/components/PropertyImageCarousel";
import { PropertyImage } from "react-migration/components/PropertyImageCarousel/PropertyImageCarousel";
import { ModalV3 } from "react-migration/components/ModalV3";
import noImagePlaceholder from "public/img/property_preview_image_placeholder.png";

export interface ComparableImagePreviewProps {
  noFullAccess: boolean;
  listingsData?: ZooplaListingsDTO;
  loading?: boolean;
  address?: string;
}

export const ComparableImagePreview: FC<ComparableImagePreviewProps> = ({
  noFullAccess = true,
  listingsData,
  loading,
  address,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const hasImages = useMemo(() => {
    return listingsData?.listings?.latest?.content && listingsData?.listings?.latest?.content?.length > 0;
  }, [listingsData]);

  const images = useMemo(() => {
    return hasImages
      ? (listingsData?.listings?.latest?.content?.map((item) => {
          return {
            filename: `https://lid.zoocdn.com/u/480/360/${item.filename}:p`,
            caption: item.caption ?? "",
            mime_type: item.mime_type ?? "",
            type: item.type ?? "property_image",
          };
        }) as PropertyImage[])
      : ([
          {
            filename: noImagePlaceholder,
            caption: noFullAccess ? null : "No images available",
            mime_type: "image/png",
            type: "image_not_available",
          },
        ] as PropertyImage[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasImages]);

  const modalImages = useMemo(() => {
    return images.map((image) => ({
      filename: typeof image.filename === "string" ? image.filename.replace("/480/360/", "/1200/900/") : image.filename,
      caption: image.caption,
      mime_type: image.mime_type,
      type: image.type,
    }));
  }, [images]);

  return (
    <div className="flex flex-col items-start justify-center gap-2 min-h-[250px] relative overflow-hidden group">
      {loading ? (
        <SkeletonLoading rows={1} showPadding={false} />
      ) : (
        <>
          <PropertyImageCarousel
            data={images}
            withFloorplan
            withThumbnails={false}
            setModalOpen={hasImages ? () => setModalOpen(true) : undefined}
          />
          {noFullAccess && (
            <div className="w-[80%] flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <HTUpgradeDataCard source={"compsAndLocal" as UpgradeEventLocation} />
            </div>
          )}
          {modalOpen && (
            <ModalV3 onClose={() => setModalOpen(false)} title={address} withCloseButton={false}>
              <PropertyImageCarousel data={modalImages} withFloorplan />
            </ModalV3>
          )}
        </>
      )}
    </div>
  );
};
